import { motion } from "framer-motion";
import { useState } from "react";
import { IMAGES } from "@/data/site";
import { FolderOpen, ArrowUpRight, X } from "lucide-react";

const DRIVE_URL =
    "https://drive.google.com/drive/folders/1AcI5fSN7Bk-pDz_dfWUQD8MAoiOa63Iq?usp=drive_link";

export const Gallery = () => {
    const [zoom, setZoom] = useState(false);

    return (
        <section
            id="gallery"
            data-testid="gallery-section"
            className="relative py-12 md:py-16 bg-[#050505] overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
                {/* Title + description on the left, hero photo on the right */}
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    <div className="lg:col-span-5">
                        <span className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                            / 09 — Gallery
                        </span>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
                            Frames From
                            <br />
                            <span className="gold-text-gradient">
                                The Program.
                            </span>
                        </h2>
                        <p className="mt-6 max-w-md text-white/65 leading-relaxed">
                            Real Young Moguls, real moments — studios, stages,
                            production sets, and classrooms. Enjoy the
                            behind-the-scenes moments of our Young Mogul
                            Ecosystem.
                        </p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9 }}
                        className="lg:col-span-7 relative"
                        data-testid="gallery-hero"
                    >
                        <button
                            type="button"
                            onClick={() => setZoom(true)}
                            aria-label="Enlarge photo"
                            className="relative overflow-hidden border border-[#D4AF37]/40 gold-glow w-full cursor-zoom-in block"
                        >
                            <img
                                src="/assets/gallery-hero.jpg"
                                alt="Young Moguls at Encina High QB Challenge"
                                className="w-full h-auto block"
                            />
                            <div className="absolute inset-0 ring-1 ring-inset ring-[#D4AF37]/10 pointer-events-none" />
                        </button>
                        {/* Corner accents */}
                        <span className="absolute -top-2 -left-2 h-6 w-6 border-t-2 border-l-2 border-[#D4AF37]" />
                        <span className="absolute -bottom-2 -right-2 h-6 w-6 border-b-2 border-r-2 border-[#D4AF37]" />
                    </motion.div>
                </div>

                {/* Full archive CTA → Google Drive */}
                <motion.a
                    href={DRIVE_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="gallery-archive-cta"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -3 }}
                    aria-label="Open the full photo archive on Google Drive"
                    className="mt-12 group block relative overflow-hidden border border-[#D4AF37]/30 hover:border-[#D4AF37] bg-gradient-to-r from-[#0A0A0A] via-[#0E0E0E] to-[#0A0A0A] hover:gold-glow transition-all duration-500"
                >
                    <div
                        className="absolute inset-0 opacity-[0.08] pointer-events-none"
                        style={{
                            backgroundImage: `url(${IMAGES.goldTexture})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            mixBlendMode: "screen",
                        }}
                    />
                    <span className="absolute -top-px -left-px h-10 w-10 border-t-2 border-l-2 border-[#D4AF37]" />
                    <span className="absolute -bottom-px -right-px h-10 w-10 border-b-2 border-r-2 border-[#D4AF37]" />

                    <div className="relative flex flex-col sm:flex-row items-center gap-6 sm:gap-10 px-6 sm:px-10 py-7 sm:py-9">
                        <div className="h-16 w-16 shrink-0 grid place-items-center border border-[#D4AF37]/40 bg-black">
                            <FolderOpen
                                size={28}
                                className="text-[#D4AF37] group-hover:scale-110 transition-transform"
                            />
                        </div>

                        <div className="flex-1 text-center sm:text-left">
                            <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-bold">
                                The Full Archive
                            </div>
                            <div className="mt-2 font-display text-2xl md:text-3xl uppercase tracking-tight leading-tight text-white group-hover:text-[#D4AF37] transition-colors">
                                Open The Photo Vault
                            </div>
                            <div className="mt-2 text-sm text-white/60 leading-relaxed max-w-2xl">
                                Years of cohorts, performances, mentor sessions,
                                and on-set moments — hosted on Google Drive and
                                updated as new chapters drop.
                            </div>
                        </div>

                        <div className="flex items-center gap-3 px-5 py-3 border border-[#D4AF37] text-[#D4AF37] uppercase tracking-[0.25em] text-xs font-bold whitespace-nowrap group-hover:bg-[#D4AF37] group-hover:text-black transition-all">
                            View On Drive
                            <ArrowUpRight
                                size={16}
                                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                            />
                        </div>
                    </div>
                </motion.a>
            </div>

            {/* Lightbox */}
            {zoom && (
                <div
                    onClick={() => setZoom(false)}
                    className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
                    data-testid="gallery-lightbox"
                >
                    <button
                        onClick={() => setZoom(false)}
                        className="absolute top-6 right-6 h-10 w-10 border border-white/20 grid place-items-center text-white hover:text-[#D4AF37] hover:border-[#D4AF37]"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                    <img
                        src="/assets/gallery-hero.jpg"
                        alt="Young Moguls at Encina High QB Challenge"
                        className="max-w-full max-h-[90vh] object-contain"
                    />
                </div>
            )}
        </section>
    );
};
